(() => {
  const motionEpoch = performance.now();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileMedia = window.matchMedia('(max-width: 640px)');
  const homeCleanups = new WeakMap();

  const padTime = (value) => String(value).padStart(2, '0');

  const syncAndPlay = (video) => {
    if (!video || reducedMotion.matches || video.hidden || document.visibilityState === 'hidden') return;
    if (video.dataset.silversDeferred === 'true' && video.dataset.silversActivated !== 'true') return;
    if (video.dataset.silversInView === 'false') return;

    if (Number.isFinite(video.duration) && video.duration > 0) {
      const elapsed = (performance.now() - motionEpoch) / 1000;
      const targetTime = elapsed % video.duration;
      if (Math.abs(video.currentTime - targetTime) > 0.09) video.currentTime = targetTime;
    }

    video.play().catch(() => {});
  };

  const initializeVideo = (video) => {
    if (video.dataset.silversInitialized === 'true') return;
    if (video.dataset.silversDeferred === 'true' && video.dataset.silversActivated !== 'true') return;
    video.dataset.silversInitialized = 'true';
    const media = video.closest('[data-silvers-media]');
    const showVideo = () => media?.classList.add('is-playing');
    const showPoster = () => media?.classList.remove('is-playing');

    video.addEventListener('loadedmetadata', () => syncAndPlay(video));
    video.addEventListener('canplay', () => {
      showVideo();
      syncAndPlay(video);
    });
    video.addEventListener('playing', showVideo);
    video.addEventListener('error', showPoster);
    video.querySelectorAll('source').forEach((source) => source.addEventListener('error', showPoster));
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) showVideo();
    videoObserver?.observe(video);
    if (reducedMotion.matches) {
      video.pause();
      return;
    }
    syncAndPlay(video);
  };

  const videoObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (!(video instanceof HTMLVideoElement)) return;
            video.dataset.silversInView = String(entry.isIntersecting);
            if (entry.isIntersecting) syncAndPlay(video);
            else video.pause();
          });
        },
        { rootMargin: '120px 0px' },
      )
    : null;

  const activateDeferredVideo = (video) => {
    if (!video || reducedMotion.matches || video.dataset.silversActivated === 'true') return;
    const source = mobileMedia.matches ? video.dataset.mobileSrc : video.dataset.desktopSrc;
    if (!source) return;

    video.dataset.silversActivated = 'true';
    video.dataset.silversSource = source;
    video.preload = 'auto';
    initializeVideo(video);
    video.src = source;
    video.load();
  };

  const initializeHome = (home) => {
    if (!home || home.dataset.silversInitialized === 'true') return;
    home.dataset.silversInitialized = 'true';

    const scene = home.querySelector('[data-home-scene]');
    const hero = home.querySelector('[data-home-role="hero"] video');
    const companions = [...home.querySelectorAll('[data-home-role="left"] video, [data-home-role="top"] video, [data-home-role="right"] video')];
    if (!scene) return;
    if (!hero) {
      scene.classList.add('is-revealed');
      return;
    }

    if (reducedMotion.matches) {
      scene.classList.add('is-revealed');
      return;
    }

    let timer;
    let warmupTimer;
    let fallbackTimer;
    const prepareCompanions = () => companions.forEach(activateDeferredVideo);
    const revealNow = () => {
      window.clearTimeout(timer);
      window.clearTimeout(warmupTimer);
      window.clearTimeout(fallbackTimer);
      prepareCompanions();
      scene.classList.add('is-revealed');
    };
    const reveal = () => {
      if (timer || scene.classList.contains('is-revealed')) return;
      const duration = Number.isFinite(hero.duration) && hero.duration > 0 ? hero.duration : 7.6;
      const durationMs = duration * 1000;
      warmupTimer = window.setTimeout(prepareCompanions, Math.max(0, durationMs - 1400));
      timer = window.setTimeout(() => {
        revealNow();
      }, durationMs);
    };

    hero.addEventListener('playing', reveal, { once: true });
    hero.addEventListener('error', revealNow, { once: true });
    fallbackTimer = window.setTimeout(revealNow, 10000);
    if (!hero.paused) reveal();

    homeCleanups.set(home, () => {
      window.clearTimeout(timer);
      window.clearTimeout(warmupTimer);
      window.clearTimeout(fallbackTimer);
      hero.removeEventListener('playing', reveal);
      hero.removeEventListener('error', revealNow);
    });
  };

  if (!customElements.get('silvers-offer-clock')) {
    customElements.define(
      'silvers-offer-clock',
      class extends HTMLElement {
        connectedCallback() {
          if (this.interval) return;
          this.output = this.querySelector('[data-offer-time]');
          this.label = this.dataset.timerLabel || '';
          this.windowMs = Math.max(1, Number(this.dataset.windowMinutes) || 120) * 60 * 1000;
          this.anchorMs = Date.parse(this.dataset.anchor || '');
          if (!Number.isFinite(this.anchorMs)) this.anchorMs = 0;
          this.update();
          this.interval = window.setInterval(() => this.update(), 1000);
        }

        disconnectedCallback() {
          window.clearInterval(this.interval);
          this.interval = null;
        }

        update() {
          if (!this.output) return;
          const elapsed = Date.now() - this.anchorMs;
          const remainder = ((elapsed % this.windowMs) + this.windowMs) % this.windowMs;
          const seconds = Math.ceil((this.windowMs - remainder) / 1000);
          const hours = Math.floor(seconds / 3600);
          const minutes = Math.floor((seconds % 3600) / 60);
          const remainingSeconds = seconds % 60;
          const value = `${padTime(hours)}:${padTime(minutes)}:${padTime(remainingSeconds)}`;
          this.output.textContent = value;
          this.output.dateTime = `PT${hours}H${minutes}M${remainingSeconds}S`;
          if (this.label) this.output.setAttribute('aria-label', `${this.label}: ${value}`);
        }
      },
    );
  }

  if (!customElements.get('silvers-product-builder')) {
    customElements.define(
      'silvers-product-builder',
      class extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.form = this.querySelector('form');
          this.product = this.closest('[data-silvers-product]');
          this.stage = this.product?.querySelector('[data-product-stage]');
          this.stack = this.product?.querySelector('[data-product-stack]');
          this.slots = [...(this.product?.querySelectorAll('[data-stage-lens]') || [])];
          this.packButtons = [...this.querySelectorAll('[data-pack-id]')];
          this.tabs = [...this.querySelectorAll('[data-lens-index]')];
          this.finishButtons = [...this.querySelectorAll('[data-finish-id]')];
          this.finishGroup = this.querySelector('[data-finish-group]');
          this.currentFinish = this.querySelector('[data-current-finish]');
          this.addButton = this.querySelector('[data-add-button]');
          this.addButtonLabel = this.querySelector('[data-add-button-label]');
          this.checkoutNote = this.querySelector('[data-checkout-note]');
          this.summary = this.querySelector('[data-selection-summary]');
          this.inventoryMessage = this.product?.querySelector('[data-inventory-message]');
          this.variantCatalog = new Map(
            [...this.querySelectorAll('[data-catalog-variant]')]
              .filter((node) => node.dataset.sku)
              .map((node) => [node.dataset.sku.toUpperCase(), node.dataset]),
          );
          this.selections = [null, null, null];
          this.activeLens = 0;
          this.selectedPack = this.packButtons.find((button) => button.getAttribute('aria-checked') === 'true') || this.packButtons.find((button) => !button.disabled);

          this.packButtons.forEach((button) => button.addEventListener('click', () => this.selectPack(button)));
          this.packButtons.forEach((button) => button.addEventListener('keydown', (event) => this.handlePackKeydown(event)));
          this.tabs.forEach((button) => button.addEventListener('click', () => this.selectLens(Number(button.dataset.lensIndex))));
          this.tabs.forEach((button) => button.addEventListener('keydown', (event) => this.handleTabKeydown(event)));
          this.slots.forEach((button) => button.addEventListener('click', () => this.selectLens(Number(button.dataset.stageLens))));
          this.finishButtons.forEach((button) => button.addEventListener('click', () => this.selectFinish(button)));
          this.finishButtons.forEach((button) => button.addEventListener('keydown', (event) => this.handleFinishKeydown(event)));
          this.form?.addEventListener('submit', (event) => this.validateSubmit(event));

          if (this.selectedPack) this.selectPack(this.selectedPack, false);
        }

        get quantity() {
          return Math.max(1, Math.min(3, Number(this.selectedPack?.dataset.packQuantity) || 1));
        }

        moveWithinGroup(event, items, activate) {
          const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
          if (!keys.includes(event.key)) return;
          const available = items.filter((item) => !item.hidden && !item.disabled);
          if (!available.length) return;

          event.preventDefault();
          const currentIndex = Math.max(0, available.indexOf(event.currentTarget));
          let nextIndex = currentIndex;
          if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = available.length - 1;
          else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % available.length;
          else nextIndex = (currentIndex - 1 + available.length) % available.length;

          const target = available[nextIndex];
          activate(target);
          target.focus({ preventScroll: true });
        }

        handlePackKeydown(event) {
          this.moveWithinGroup(event, this.packButtons, (button) => this.selectPack(button, false));
        }

        handleTabKeydown(event) {
          this.moveWithinGroup(event, this.tabs, (button) => this.selectLens(Number(button.dataset.lensIndex)));
        }

        handleFinishKeydown(event) {
          this.moveWithinGroup(event, this.finishButtons, (button) => this.selectFinish(button, false));
        }

        selectPack(button, moveFocus = true) {
          if (!button || button.disabled) return;
          this.selectedPack = button;
          this.packButtons.forEach((candidate) => {
            const selected = candidate === button;
            candidate.classList.toggle('is-selected', selected);
            candidate.setAttribute('aria-checked', String(selected));
            candidate.tabIndex = selected ? 0 : -1;
          });

          const packProperty = this.querySelector('[data-pack-property]');
          if (packProperty) packProperty.value = button.dataset.packName || '';

          const firstMissing = this.selections.slice(0, this.quantity).findIndex((selection) => !selection);
          this.activeLens = firstMissing >= 0 ? firstMissing : Math.min(this.activeLens, this.quantity - 1);
          this.render();
          if (moveFocus) this.tabs[this.activeLens]?.focus({ preventScroll: true });
        }

        selectLens(index) {
          if (!Number.isInteger(index) || index < 0 || index >= this.quantity) return;
          this.activeLens = index;
          this.render();
        }

        selectFinish(button, autoAdvance = true) {
          this.selections[this.activeLens] = {
            id: button.dataset.finishId,
            code: button.dataset.finishCode,
            name: button.dataset.finishName,
            sku: button.dataset.finishSku,
            video: button.dataset.video,
            mobileVideo: button.dataset.mobileVideo,
            poster: button.dataset.poster,
          };

          const nextMissing = this.selections.slice(0, this.quantity).findIndex((selection) => !selection);
          if (autoAdvance && nextMissing >= 0 && nextMissing !== this.activeLens) this.activeLens = nextMissing;
          this.render();
        }

        resolveVariant() {
          const visible = this.selections.slice(0, this.quantity);
          if (visible.some((selection) => !selection?.code)) return null;
          const order = new Map(['CP', 'CO', 'OO', 'OP'].map((code, index) => [code, index]));
          const codes = visible
            .map((selection) => selection.code.toUpperCase())
            .sort((left, right) => order.get(left) - order.get(right));
          const sku = this.quantity === 1 ? `SLV-${codes[0]}` : `SLV-P${this.quantity}-${codes.join('-')}`;
          const variant = this.variantCatalog.get(sku);
          return variant ? { ...variant, sku } : null;
        }

        updateSlotMedia(slot, selection, visible) {
          const video = slot.querySelector('[data-slot-video]');
          const placeholder = slot.querySelector('[data-slot-placeholder]');
          const empty = slot.querySelector('[data-slot-empty]');
          if (!video || !placeholder || !empty) return;

          if (!visible || !selection) {
            video.hidden = true;
            video.classList.remove('is-playing');
            video.pause();
            placeholder.hidden = false;
            if (selection?.poster && placeholder instanceof HTMLImageElement) placeholder.src = selection.poster;
            empty.hidden = Boolean(selection);
            return;
          }

          const source = mobileMedia.matches ? selection.mobileVideo : selection.video;
          if (!source) {
            video.hidden = true;
            video.classList.remove('is-playing');
            video.pause();
            if (selection.poster && placeholder instanceof HTMLImageElement) placeholder.src = selection.poster;
            placeholder.hidden = false;
            empty.hidden = true;
            return;
          }
          if (video.dataset.silversStageEvents !== 'true') {
            video.dataset.silversStageEvents = 'true';
            video.addEventListener('canplay', () => video.classList.add('is-playing'));
            video.addEventListener('playing', () => video.classList.add('is-playing'));
            video.addEventListener('error', () => video.classList.remove('is-playing'));
            videoObserver?.observe(video);
          }
          if (video.dataset.finishId !== selection.id || video.dataset.source !== source) {
            video.dataset.finishId = selection.id;
            video.dataset.source = source;
            video.classList.remove('is-playing');
            video.poster = selection.poster;
            video.src = source;
            video.load();
          }

          video.hidden = false;
          if (selection.poster && placeholder instanceof HTMLImageElement) placeholder.src = selection.poster;
          placeholder.hidden = false;
          empty.hidden = true;
          syncAndPlay(video);
        }

        render() {
          const quantity = this.quantity;
          const configured = this.selections.slice(0, quantity).filter(Boolean).length;
          const missing = quantity - configured;
          const resolvedVariant = missing === 0 ? this.resolveVariant() : null;

          if (this.stage) {
            this.stage.dataset.quantity = String(quantity);
            this.stage.setAttribute('aria-label', this.dataset.packView.replace('__COUNT__', String(quantity)));
          }
          if (this.stack) this.stack.className = `silvers-stack silvers-stack--${quantity}`;

          this.slots.forEach((slot, index) => {
            const visible = index < quantity;
            const active = visible && index === this.activeLens;
            const selection = this.selections[index];
            slot.disabled = !visible;
            slot.classList.toggle('is-visible', visible);
            slot.classList.toggle('is-active', active);
            slot.classList.toggle('is-empty', !selection);
            slot.classList.toggle('is-configured', Boolean(selection));
            slot.setAttribute('aria-pressed', String(active));
            const name = slot.querySelector('[data-slot-name]');
            if (name) name.textContent = selection?.name || this.dataset.chooseFinish;
            this.updateSlotMedia(slot, selection, visible);
          });

          this.tabs.forEach((tab, index) => {
            const visible = index < quantity;
            const active = visible && index === this.activeLens;
            const selection = this.selections[index];
            tab.hidden = !visible;
            tab.classList.toggle('is-active', active);
            tab.classList.toggle('is-pending', !selection);
            tab.classList.toggle('is-complete', Boolean(selection));
            tab.setAttribute('aria-selected', String(active));
            tab.tabIndex = active ? 0 : -1;
            const name = tab.querySelector('[data-lens-name]');
            if (name) name.textContent = selection?.name || this.dataset.chooseFinish;
          });

          const activeSelection = this.selections[this.activeLens];
          if (this.currentFinish) this.currentFinish.textContent = activeSelection?.name || this.dataset.chooseFinish;
          if (this.finishGroup) this.finishGroup.setAttribute('aria-label', this.dataset.finishForLens.replace('__NUMBER__', String(this.activeLens + 1)));
          this.finishButtons.forEach((button) => {
            const selected = button.dataset.finishId === activeSelection?.id;
            button.classList.toggle('is-selected', selected);
            button.setAttribute('aria-checked', String(selected));
            const firstAvailable = this.finishButtons.find((candidate) => !candidate.disabled);
            button.tabIndex = selected || (!activeSelection && button === firstAvailable) ? 0 : -1;
          });

          const visibleSelections = this.selections.slice(0, quantity);
          visibleSelections.forEach((selection, index) => {
            const finishProperty = this.querySelector(`[data-finish-property="${index + 1}"]`);
            const skuProperty = this.querySelector(`[data-sku-property="${index + 1}"]`);
            const enabled = Boolean(selection);
            if (finishProperty) {
              finishProperty.disabled = !enabled;
              finishProperty.value = selection?.name || '';
            }
            if (skuProperty) {
              skuProperty.disabled = !enabled;
              skuProperty.value = selection?.sku || '';
            }
          });
          for (let index = quantity; index < 3; index += 1) {
            const finishProperty = this.querySelector(`[data-finish-property="${index + 1}"]`);
            const skuProperty = this.querySelector(`[data-sku-property="${index + 1}"]`);
            if (finishProperty) finishProperty.disabled = true;
            if (skuProperty) skuProperty.disabled = true;
          }


          const variantInput = this.querySelector('[data-variant-input]');
          const parentSkuProperty = this.querySelector('[data-parent-sku-property]');
          if (variantInput) variantInput.value = resolvedVariant?.variantId || '';
          if (parentSkuProperty) parentSkuProperty.value = resolvedVariant?.sku || '';

          if (this.inventoryMessage) {
            const inventory = Number(resolvedVariant?.inventory);
            const showLowInventory = resolvedVariant?.inventoryTracked === 'true' && inventory > 0 && inventory < 18;
            this.inventoryMessage.hidden = !showLowInventory;
          }

          this.product?.querySelectorAll('[data-ready-count], [data-builder-ready]').forEach((node) => {
            node.textContent = String(configured);
          });
          this.product?.querySelectorAll('[data-ready-total], [data-builder-total]').forEach((node) => {
            node.textContent = String(quantity);
          });

          if (this.addButton) {
            const configurationAvailable = resolvedVariant?.available === 'true';
            const addButtonLabel = this.addButtonLabel || this.addButton;
            this.addButton.disabled = missing > 0 || !configurationAvailable;
            if (missing === 0 && configurationAvailable) {
              addButtonLabel.textContent = `${this.selectedPack.dataset.cta} · ${resolvedVariant.price}`;
            } else if (missing === 0) {
              addButtonLabel.textContent = this.dataset.configurationUnavailable;
            } else if (missing === 1) {
              addButtonLabel.textContent = this.dataset.missingOne;
            } else {
              addButtonLabel.textContent = this.dataset.missingMany.replace('__COUNT__', String(missing));
            }
          }

          if (this.checkoutNote) {
            this.checkoutNote.textContent = missing === 0
              ? resolvedVariant?.available === 'true' ? this.dataset.allReady : this.dataset.configurationUnavailable
              : this.dataset.configured.replace('__READY__', String(configured)).replace('__TOTAL__', String(quantity));
          }

          if (this.summary) {
            this.summary.textContent = visibleSelections
              .map((selection, index) => `${padTime(index + 1)} ${selection?.name || this.dataset.chooseFinish}`)
              .join(' · ');
          }
        }

        validateSubmit(event) {
          const missingIndex = this.selections.slice(0, this.quantity).findIndex((selection) => !selection);
          const resolvedVariant = missingIndex < 0 ? this.resolveVariant() : null;
          if (missingIndex < 0 && resolvedVariant?.available === 'true') return;
          event.preventDefault();
          this.activeLens = Math.max(0, missingIndex);
          this.render();
          this.tabs[this.activeLens]?.focus();
        }
      },
    );
  }

  const initialize = (root = document) => {
    root.querySelectorAll('[data-silvers-home]').forEach(initializeHome);
    root.querySelectorAll('[data-silvers-video]').forEach(initializeVideo);
  };

  initialize();

  document.addEventListener('visibilitychange', () => {
    const videos = document.querySelectorAll('[data-silvers-video], [data-slot-video]');
    if (document.visibilityState !== 'visible') {
      videos.forEach((video) => video.pause());
      return;
    }
    videos.forEach(syncAndPlay);
  });

  mobileMedia.addEventListener('change', () => {
    document.querySelectorAll('[data-silvers-video][data-silvers-activated="true"]').forEach((video) => {
      const source = mobileMedia.matches ? video.dataset.mobileSrc : video.dataset.desktopSrc;
      if (!source || video.dataset.silversSource === source) return;
      video.dataset.silversSource = source;
      video.src = source;
      video.load();
    });
    document.querySelectorAll('silvers-product-builder').forEach((builder) => builder.render?.());
  });

  reducedMotion.addEventListener('change', () => {
    const videos = document.querySelectorAll('[data-silvers-video], [data-slot-video]');
    if (reducedMotion.matches) {
      videos.forEach((video) => video.pause());
      document.querySelectorAll('[data-home-scene]').forEach((scene) => scene.classList.add('is-revealed'));
      return;
    }

    document.querySelectorAll('[data-silvers-video][data-silvers-deferred="true"]').forEach(activateDeferredVideo);
    videos.forEach(syncAndPlay);
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-silvers-home]').forEach((home) => homeCleanups.get(home)?.());
    event.target.querySelectorAll('[data-silvers-video], [data-slot-video]').forEach((video) => videoObserver?.unobserve(video));
  });
})();
